import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, Menu, Mic, Monitor, Settings as SettingsIcon, Square } from "lucide-react";
import { FileViewer } from "./components/FileViewer.js";
import { Login } from "./components/Login.js";
import { RightPanel } from "./components/RightPanel.js";
import { Settings } from "./components/Settings.js";
import { Sidebar } from "./components/Sidebar.js";
import { Timeline } from "./components/Timeline.js";
import { Waveform } from "./components/Waveform.js";
import { api } from "./lib/api.js";
import { conversations as convApi, messageToItem, type ConvMeta, type ConvMessage } from "./lib/conversations.js";
import { EventStream } from "./lib/events.js";
import { RealtimeClient, type RealtimeStatus } from "./lib/realtime.js";
import type { AtlasEvent, TimelineItem } from "./types.js";

let counter = 0;
const nextId = () => `i${Date.now()}-${counter++}`;

function sessionIdFromPath(): string | null {
  const m = location.pathname.match(/^\/session\/([^/]+)$/);
  return m ? decodeURIComponent(m[1]) : null;
}
function setUrl(id: string | null, replace = false): void {
  const path = id ? `/session/${encodeURIComponent(id)}` : "/";
  if (location.pathname === path) return;
  if (replace) history.replaceState({}, "", path);
  else history.pushState({}, "", path);
}

export function App() {
  const [authed, setAuthed] = useState(false);
  const [status, setStatus] = useState<RealtimeStatus>("idle");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [text, setText] = useState("");

  const [convs, setConvs] = useState<ConvMeta[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showText, setShowText] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [filePath, setFilePath] = useState<string | null>(null);

  const rtRef = useRef<RealtimeClient | null>(null);
  const activeIdRef = useRef<string | null>(null);
  const creatingRef = useRef<Promise<string> | null>(null);
  activeIdRef.current = activeId;

  const push = useCallback((item: Omit<TimelineItem, "id">) => {
    setItems((prev) => [...prev.slice(-300), { ...item, id: nextId() }]);
  }, []);

  // Streaming text: voice reply + worker output stream token-by-token into a bubble.
  const voiceStream = useRef<{ id: string; text: string } | null>(null);
  const workerStream = useRef<{ id: string; text: string } | null>(null);
  // Streaming reasoning: accumulates into a collapsible "Thought for Xs" block.
  const thinkingStream = useRef<{ id: string; text: string; start: number } | null>(null);

  const streamInto = useCallback((ref: typeof voiceStream, delta: string) => {
    if (!ref.current) {
      const id = nextId();
      ref.current = { id, text: delta };
      setItems((prev) => [...prev.slice(-300), { id, kind: "assistant", text: delta, ts: Date.now() }]);
    } else {
      ref.current.text += delta;
      const { id, text } = ref.current;
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, text } : it)));
    }
  }, []);

  const finalizeStream = useCallback((ref: typeof voiceStream, finalText: string): string | null => {
    if (!ref.current) return null;
    const { id } = ref.current;
    const text = finalText || ref.current.text;
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, text } : it)));
    ref.current = null;
    return text;
  }, []);

  const refreshConvs = useCallback(async () => {
    try {
      setConvs(await convApi.list());
    } catch {
      /* ignore */
    }
  }, []);

  // Ensure there's an active conversation (create lazily, race-guarded).
  const ensureActive = useCallback(async (): Promise<string> => {
    if (activeIdRef.current) return activeIdRef.current;
    if (creatingRef.current) return creatingRef.current;
    creatingRef.current = (async () => {
      const meta = await convApi.create();
      activeIdRef.current = meta.id;
      setActiveId(meta.id);
      setUrl(meta.id, true);
      await refreshConvs();
      return meta.id;
    })();
    const id = await creatingRef.current;
    creatingRef.current = null;
    return id;
  }, [refreshConvs]);

  const persist = useCallback(
    async (role: ConvMessage["role"], text: string, kind?: string, url?: string) => {
      try {
        const id = await ensureActive();
        await convApi.appendMessage(id, { role, text, kind, url });
        void refreshConvs();
      } catch {
        /* best-effort */
      }
    },
    [ensureActive, refreshConvs],
  );

  // Initial load: fetch the list, then restore the /session/<id> from the URL,
  // or fall back to the most recent chat so reload never lands on a blank screen.
  useEffect(() => {
    if (!authed) return;
    void (async () => {
      const list = await convApi.list().catch(() => [] as ConvMeta[]);
      setConvs(list);
      const urlId = sessionIdFromPath();
      if (urlId) {
        void loadConv(urlId);
      } else if (list.length > 0) {
        setUrl(list[0].id, true);
        void loadConv(list[0].id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // Live worker events.
  const onAtlasEvent = useCallback(
    (ev: AtlasEvent) => {
      // Only show live activity for the conversation currently on screen.
      const cid = (ev as { conversationId?: string }).conversationId;
      if (cid && cid !== activeIdRef.current) return;
      switch (ev.type) {
        case "status":
          setBusy(ev.phase !== "idle" && ev.phase !== "done" && ev.phase !== "error");
          // Show tool activity (searching, running, etc.) plus blockers/errors.
          if (
            ev.phase === "preamble" ||
            ev.phase === "step" ||
            ev.phase === "blocked" ||
            ev.phase === "error"
          ) {
            push({ kind: "status", text: ev.message, phase: ev.phase, ts: ev.ts });
          }
          if (ev.tool === "computer_use" && ev.phase === "preamble") setDesktopOpen(true);
          break;
        case "worker_tool":
          push({ kind: "tool", text: ev.summary, ts: ev.ts });
          break;
        case "assistant_delta":
          streamInto(workerStream, ev.delta);
          break;
        case "thinking":
          if (ev.done) {
            const cur = thinkingStream.current;
            if (cur) {
              const text = ev.text || cur.text;
              const durationMs = ev.durationMs ?? Date.now() - cur.start;
              setItems((prev) =>
                prev.map((it) => (it.id === cur.id ? { ...it, text, durationMs } : it)),
              );
              thinkingStream.current = null;
            } else if (ev.text) {
              push({ kind: "thinking", text: ev.text, durationMs: ev.durationMs, ts: ev.ts });
            }
          } else if (ev.delta) {
            const delta = ev.delta;
            const cur = thinkingStream.current;
            if (!cur) {
              const id = nextId();
              thinkingStream.current = { id, text: delta, start: Date.now() };
              setItems((prev) => [
                ...prev.slice(-300),
                { id, kind: "thinking", text: delta, ts: Date.now() },
              ]);
            } else {
              cur.text += ev.delta;
              setItems((prev) =>
                prev.map((it) => (it.id === cur.id ? { ...it, text: cur.text } : it)),
              );
            }
          }
          break;
        case "screenshot":
          // Not shown in chat — the live desktop panel is the place to watch.
          break;
        case "result":
          // Finalize the streamed worker bubble, or render fresh if nothing streamed.
          // Persistence happens server-side (gateway) so it saves to the right chat.
          if (!finalizeStream(workerStream, ev.summary)) {
            push({ kind: "result", text: ev.summary, ts: ev.ts });
          }
          setBusy(false);
          break;
        default:
          break;
      }
    },
    [push, streamInto, finalizeStream],
  );

  useEffect(() => {
    if (!authed) return;
    const stream = new EventStream(onAtlasEvent);
    stream.start();
    return () => stream.stop();
  }, [authed, onAtlasEvent]);

  // Drop the shared realtime session (used when switching conversations).
  const dropSession = useCallback(() => {
    rtRef.current?.disconnect();
    rtRef.current = null;
    setListening(false);
    voiceStream.current = null;
    workerStream.current = null;
    thinkingStream.current = null;
  }, []);

  // --- Conversation management --------------------------------------------
  // Load a conversation WITHOUT touching the URL (used by routing handlers).
  const loadConv = useCallback(
    async (id: string) => {
      dropSession();
      try {
        const { messages } = await convApi.get(id);
        setItems(messages.map(messageToItem));
        setActiveId(id);
        activeIdRef.current = id;
      } catch {
        /* ignore (e.g. deleted/unknown id) */
      }
    },
    [dropSession],
  );

  const selectConv = useCallback(
    async (id: string) => {
      setSidebarOpen(false);
      if (id === activeIdRef.current) return;
      setUrl(id);
      await loadConv(id);
    },
    [loadConv],
  );

  const newChat = useCallback(async () => {
    setSidebarOpen(false);
    dropSession();
    setItems([]);
    const meta = await convApi.create();
    setActiveId(meta.id);
    activeIdRef.current = meta.id;
    setUrl(meta.id);
    await refreshConvs();
  }, [refreshConvs, dropSession]);

  const renameConv = useCallback(async (id: string, title: string) => {
    await convApi.update(id, { title });
    await refreshConvs();
  }, [refreshConvs]);

  const pinConv = useCallback(async (id: string, pinned: boolean) => {
    await convApi.update(id, { pinned });
    await refreshConvs();
  }, [refreshConvs]);

  const deleteConv = useCallback(async (id: string) => {
    await convApi.remove(id);
    if (activeIdRef.current === id) {
      setActiveId(null);
      activeIdRef.current = null;
      setItems([]);
      setUrl(null);
    }
    await refreshConvs();
  }, [refreshConvs]);

  // Back/forward navigation switches sessions.
  useEffect(() => {
    if (!authed) return;
    const onPop = () => {
      const id = sessionIdFromPath();
      if (id && id !== activeIdRef.current) void loadConv(id);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [authed, loadConv]);

  // --- One realtime session owns the conversation; text + voice share it ----
  const makeClient = useCallback(
    () =>
      new RealtimeClient({
        onStatus: setStatus,
        onUserTranscript: (t) => {
          if (!t.trim()) return;
          push({ kind: "you", text: t, ts: Date.now() });
          void persist("user", t);
        },
        onAssistantDelta: (d) => streamInto(voiceStream, d),
        onAssistantTranscript: (t) => {
          const finalized = finalizeStream(voiceStream, t);
          if (finalized !== null) {
            void persist("assistant", finalized);
          } else if (t.trim()) {
            push({ kind: "assistant", text: t, ts: Date.now() });
            void persist("assistant", t);
          }
        },
        onToolCall: (tool) => {
          if (tool === "computer_use") setDesktopOpen(true);
        },
        onError: (m) => push({ kind: "status", text: m, phase: "error", ts: Date.now() }),
        getConversationId: () => activeIdRef.current,
      }),
    [push, persist, streamInto, finalizeStream],
  );

  // Open the shared channel if needed. listening=false => text mode (no mic cost).
  const ensureConnected = useCallback(
    async (wantListening: boolean) => {
      const id = await ensureActive();
      const rt = rtRef.current ?? makeClient();
      rtRef.current = rt;
      if (!rt.isConnected()) {
        await rt.connect({ listening: wantListening, conversationId: id });
        setListening(wantListening && rt.hasMic());
      }
      return rt;
    },
    [ensureActive, makeClient],
  );

  const toggleMic = useCallback(async () => {
    const rt = rtRef.current;
    if (rt?.isConnected()) {
      if (listening) {
        rt.setListening(false);
        setListening(false);
      } else if (rt.setListening(true)) {
        setListening(true);
      } else {
        // Connected text-only (no mic) -> reconnect with mic to start listening.
        rt.disconnect();
        rtRef.current = null;
        try {
          const fresh = await ensureConnected(true);
          setListening(fresh.hasMic());
        } catch {
          /* surfaced via onError */
        }
      }
      return;
    }
    try {
      await ensureConnected(true);
    } catch {
      /* surfaced via onError */
    }
  }, [listening, ensureConnected]);

  const sendText = useCallback(async () => {
    const t = text.trim();
    if (!t) return;
    push({ kind: "you", text: t, ts: Date.now() });
    void persist("user", t);
    setText("");
    try {
      const rt = await ensureConnected(false); // open channel, not listening
      rt.sendText(t);
    } catch (err) {
      push({ kind: "status", text: (err as Error).message, phase: "error", ts: Date.now() });
    }
  }, [text, push, persist, ensureConnected]);

  // Hard stop: cancel the voice response AND abort the worker/computer-use loop,
  // independent of whether the model decides to call abort.
  const stopAll = useCallback(async () => {
    rtRef.current?.cancel();
    setBusy(false);
    const id = activeIdRef.current;
    if (id) await api.callTool("abort", {}, undefined, id).catch(() => {});
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => {});
    location.reload();
  }, []);

  if (!authed) return <Login onAuthed={() => setAuthed(true)} />;

  const activeTitle = convs.find((c) => c.id === activeId)?.title;
  // mic button reflects LISTENING state (channel may be open for text without mic).
  const micClass =
    status === "connecting" ? "connecting" : listening ? (busy ? "busy" : "live") : "";
  const voiceLabel =
    status === "connecting"
      ? "Connecting…"
      : listening
        ? busy
          ? "Working…"
          : "Listening — tap to stop"
        : status === "error"
          ? "Tap to retry"
          : "Tap to talk";

  return (
    <div className={`shell ${desktopOpen ? "with-right" : ""}`}>
      <Sidebar
        open={sidebarOpen}
        conversations={convs}
        activeId={activeId}
        onClose={() => setSidebarOpen(false)}
        onSelect={selectConv}
        onNew={newChat}
        onRename={renameConv}
        onPin={pinConv}
        onDelete={deleteConv}
        onLogout={logout}
      />

      <main className="center">
        <header className="chead">
          <button className="ghost icon hamburger" onClick={() => setSidebarOpen(true)} aria-label="Menu">
            <Menu size={17} />
          </button>
          <span className="chead-title">{activeTitle || "New session"}</span>
          <span className="badge">Opus 4.8</span>
          <div className="chead-actions">
            <button className="ghost btn-icon" onClick={() => setDesktopOpen((v) => !v)}>
              <Monitor size={15} />
              <span>Desktop</span>
            </button>
            <button className="ghost icon" onClick={() => setSettingsOpen(true)} aria-label="Settings">
              <SettingsIcon size={16} />
            </button>
            <span className={`dot ${status}`} title={status} />
          </div>
        </header>

        <Timeline items={items} busy={busy} onOpenFile={setFilePath} />

        {/* Mobile-first voice dock: big talk button, text hidden by default. */}
        <div className="voice-dock">
          {listening && (
            <div className="wave-strip">
              <Waveform getAnalyser={() => rtRef.current?.getAnalyser() ?? null} active={listening} />
            </div>
          )}
          <button
            className={`big-voice ${micClass}`}
            onClick={toggleMic}
            aria-label={listening ? "Stop listening" : "Start talking"}
          >
            {listening ? <Square size={34} /> : <Mic size={38} />}
          </button>
          <div className="bv-label">{voiceLabel}</div>
          {busy ? (
            <button className="bv-stop" onClick={stopAll}>
              <Square size={13} /> Stop
            </button>
          ) : (
            <button className="bv-type" onClick={() => setShowText((v) => !v)}>
              {showText ? "Hide keyboard" : "Type instead"}
            </button>
          )}
        </div>

        <div className={`composer ${showText ? "show" : ""}`}>
          {listening && (
            <div className="wave-strip">
              <Waveform getAnalyser={() => rtRef.current?.getAnalyser() ?? null} active={listening} />
            </div>
          )}
          <div className="composer-inner">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendText()}
              placeholder="Ask Atlas to build features, fix bugs, or work on your code…"
            />
            <button
              className={`icon-btn mic ${micClass}`}
              onClick={toggleMic}
              aria-label={listening ? "Stop listening" : "Talk"}
              title={listening ? "Stop listening" : "Talk"}
            >
              {listening ? <Square size={15} /> : <Mic size={16} />}
            </button>
            {busy ? (
              <button className="icon-btn stop" onClick={stopAll} aria-label="Stop" title="Stop">
                <Square size={15} />
              </button>
            ) : (
              <button className="icon-btn send" onClick={sendText} disabled={!text.trim()} aria-label="Send">
                <ArrowUp size={16} />
              </button>
            )}
          </div>
        </div>
      </main>

      {desktopOpen && <RightPanel open onClose={() => setDesktopOpen(false)} />}
      <Settings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <FileViewer path={filePath} onClose={() => setFilePath(null)} />
    </div>
  );
}
